import { useState, useCallback } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { useTranslation } from "../../i18n";
import {
  useSegmentDefinitions,
  useCreateSegment,
  useDeleteSegment,
  usePreviewSegmentQuery,
} from "../../features/super/hooks/useCampaigns";
import toast from "react-hot-toast";

const QUERY_PLACEHOLDER = `SELECT c.id AS customer_id
FROM customer c
JOIN registered_customer rc ON rc.customer_id = c.id
WHERE rc.send_marketing = true
  AND c.minutes_balance > 500`;

/**
 * Manager for operator-defined snapshot segments.
 *
 * A segment is the SELECT that produces its audience, pasted here instead of
 * being assembled from fixed fields: the built-in segments cover the RFM
 * buckets, and everything else ("VIP whose expiry passed 60–90 days ago and who
 * still has a balance") needs a query. The contract is one statement
 * returning exactly one integer column named `customer_id`.
 *
 * The query is previewed before it is saved, and the preview is the only way
 * to tell a query that selects nobody from one that selects everybody — so it
 * shows the first ten customers by name and phone, not a count.
 */
export default function SegmentManager({ campaignId = null }) {
  const { t } = useTranslation();
  const { data, isLoading } = useSegmentDefinitions(campaignId);
  const createMutation = useCreateSegment();
  const deleteMutation = useDeleteSegment();
  const previewMutation = usePreviewSegmentQuery();
  const [showHelp, setShowHelp] = useState(false);
  const [form, setForm] = useState({ name: "", query: "" });

  const definitions = data?.items ?? [];
  const preview = previewMutation.data;
  // A preview describes the query as it was when it ran; keeping it on screen
  // after the query is edited would show an audience the operator no longer
  // asked for. The comparison ignores the trailing semicolon the server
  // strips, so a fresh preview never reads as stale.
  const previewIsStale =
    preview != null && !sameQuery(preview.query, form.query);

  const reset = useCallback(() => {
    setForm({ name: "", query: "" });
    previewMutation.reset();
  }, [previewMutation]);

  const handlePreview = async () => {
    if (!form.query.trim()) {
      toast.error(t("campaign.segmentQueryRequired"));
      return;
    }
    try {
      await previewMutation.mutateAsync(form.query);
    } catch (err) {
      // The backend answers with the reason the query was refused (a keyword,
      // a second statement, the wrong column, a timeout) — show it as is.
      toast.error(describe(err, t("campaign.segmentPreviewFailed")));
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error(t("campaign.segmentNameRequired"));
      return;
    }
    try {
      await createMutation.mutateAsync({
        name: form.name.trim(),
        campaignId,
        query: form.query,
      });
      toast.success(t("campaign.segmentCreated"));
      reset();
    } catch (err) {
      toast.error(describe(err, t("campaign.segmentCreateFailed")));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(t("campaign.segmentDeleted"));
    } catch (err) {
      toast.error(describe(err, t("campaign.segmentDeleteFailed")));
    }
  };

  const busy = createMutation.isPending;

  return (
    <div className="border border-c-gray-400/30 rounded-lg p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{t("campaign.customSegments")}</h3>
        <button
          type="button"
          onClick={() => setShowHelp(!showHelp)}
          aria-expanded={showHelp}
          aria-label={t("campaign.segmentHelp")}
          title={t("campaign.segmentHelp")}
          className="w-5 h-5 rounded-full border border-c-gray-400/50 text-c-gray-300 text-xs leading-none flex items-center justify-center hover:text-white"
        >
          ?
        </button>
      </div>

      {showHelp && (
        <div className="bg-c-gray-500/60 border border-c-gray-400/20 rounded-lg p-3 text-xs text-c-gray-300 space-y-2">
          <p>{t("campaign.segmentHelpIntro")}</p>
          <p className="font-medium text-white">
            {t("campaign.segmentHelpContractTitle")}
          </p>
          <pre className="bg-c-gray-600/60 rounded p-2 overflow-x-auto text-[11px] leading-relaxed">
            {`-- один SELECT, одна колонка customer_id
SELECT c.id AS customer_id
FROM customer c
WHERE ...`}
          </pre>
          <p>{t("campaign.segmentHelpContractNote")}</p>
        </div>
      )}

      {isLoading ? (
        <p className="text-c-gray-400 text-xs">{t("common.loading")}</p>
      ) : definitions.length === 0 ? (
        <p className="text-c-gray-400 text-xs">
          {t("campaign.noCustomSegments")}
        </p>
      ) : (
        <ul className="space-y-1">
          {definitions.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-2 text-xs bg-c-gray-500/40 rounded px-2 py-1"
            >
              <span className="min-w-0 truncate">
                <code className="text-accent">{d.name}</code>
                <span className="text-c-gray-400"> — {describeDef(d)}</span>
                {d.isGlobal && (
                  <span className="ml-1 text-c-gray-500">
                    ({t("campaign.segmentGlobal")})
                  </span>
                )}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDelete(d.id)}
                disabled={deleteMutation.isPending}
                aria-label={t("campaign.segmentDelete")}
              >
                ✕
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-1">
        <Label className="text-xs" htmlFor="segment-name">
          {t("campaign.segmentName")}
        </Label>
        <Input
          id="segment-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="vip_sleeping_90d"
          disabled={busy}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs" htmlFor="segment-query">
          {t("campaign.segmentQuery")}
        </Label>
        <textarea
          id="segment-query"
          value={form.query}
          onChange={(e) => setForm({ ...form, query: e.target.value })}
          rows={7}
          spellCheck={false}
          placeholder={QUERY_PLACEHOLDER}
          disabled={busy}
          className="flex w-full rounded-lg border border-c-gray-400/30 bg-c-gray-500 px-3 py-2 text-sm text-white placeholder:text-c-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent font-mono resize-y"
        />
        <p className="text-c-gray-500 text-xs">
          {t("campaign.segmentQueryHint")}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={handlePreview}
          disabled={previewMutation.isPending || busy}
        >
          {previewMutation.isPending
            ? t("campaign.segmentPreviewing")
            : t("campaign.segmentPreview")}
        </Button>
        <Button size="sm" onClick={handleCreate} disabled={busy}>
          {t("campaign.segmentAdd")}
        </Button>
      </div>

      {previewIsStale && (
        <p className="text-c-gray-400 text-xs">
          {t("campaign.segmentPreviewStale")}
        </p>
      )}

      {preview && !previewIsStale && (
        <div className="space-y-2">
          <p className="text-xs text-c-gray-300">
            {preview.total == null
              ? t("campaign.segmentPreviewUnknownTotal")
              : t("campaign.segmentPreviewTotal", { total: preview.total })}
          </p>
          {preview.rows.length === 0 ? (
            <p className="text-c-gray-400 text-xs">
              {t("campaign.segmentPreviewEmpty")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">ID</TableHead>
                  <TableHead className="text-xs">
                    {t("campaign.segmentPreviewName")}
                  </TableHead>
                  <TableHead className="text-xs">
                    {t("campaign.segmentPreviewPhone")}
                  </TableHead>
                  <TableHead className="text-xs text-right">
                    {t("campaign.segmentPreviewBalance")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((row) => (
                  <TableRow key={row.customerId}>
                    <TableCell className="text-xs py-1">
                      {row.customerId}
                    </TableCell>
                    <TableCell className="text-xs py-1">
                      {[row.firstName, row.lastName]
                        .filter(Boolean)
                        .join(" ") || "—"}
                    </TableCell>
                    <TableCell className="text-xs py-1">
                      {row.phone || "—"}
                    </TableCell>
                    <TableCell className="text-xs py-1 text-right">
                      {row.minutesBalance}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      <div className="flex justify-between items-center gap-2">
        <p className="text-c-gray-500 text-xs">
          {campaignId == null
            ? t("campaign.segmentScopeGlobal")
            : t("campaign.segmentScopeCampaign")}
        </p>
      </div>
    </div>
  );
}

/** First line of the query, which is what identifies it in a one-line list. */
function describeDef(d) {
  const first =
    String(d.query || "")
      .trim()
      .split("\n")[0] || "—";
  return first.length > 80 ? `${first.slice(0, 79)}…` : first;
}

/**
 * Whether a preview still describes what is in the editor.
 *
 * The server answers with the statement it actually ran — trimmed, without the
 * trailing semicolon most editors add on paste — so both sides are normalised
 * the same way before being compared.
 */
function sameQuery(a, b) {
  const normalise = (q) =>
    String(q || "")
      .trim()
      .replace(/;$/, "")
      .trim();
  return normalise(a) === normalise(b);
}

function describe(err, fallback) {
  if (typeof err === "string" && err.trim()) return err;
  if (err?.message) return err.message;
  return fallback;
}
